// src/components/cards/GoogleTrendsCard.tsx
import React from 'react';
import DataStatusIndicator, { DataStatus } from '../DataStatusIndicator';

interface GoogleTrendsCardProps {
  dataStatus?: DataStatus;
  lastUpdated?: number;
  trendValue?: number; // Accept trend value from parent
}

const GoogleTrendsCard: React.FC<GoogleTrendsCardProps> = ({
  dataStatus = 'live',
  lastUpdated = Date.now(),
  trendValue
}) => {
  // Show error state if no trend value
  const hasData = trendValue !== undefined && trendValue !== null;
  const displayValue = hasData ? trendValue : 0;

  console.log('📊 GoogleTrendsCard received trendValue:', trendValue, 'displayValue:', displayValue);
  
  // Get color based on search interest level
  const getInterestColor = (value: number): string => {
    if (value >= 70) return '#008000'; // Very High Interest
    if (value >= 50) return '#90EE90'; // High Interest  
    if (value >= 30) return '#FFD700'; // Moderate Interest
    if (value >= 15) return '#FF6B6B'; // Low Interest
    return '#EA3943'; // Very Low Interest
  };

  // Get interest level label based on Google Trends score
  const getInterestLevel = (value: number): string => {
    if (value >= 70) return 'Very High Interest';
    if (value >= 50) return 'High Interest';
    if (value >= 30) return 'Moderate Interest';
    if (value >= 15) return 'Low Interest';
    return 'Very Low Interest';
  };

  return (
    <div className="bg-dashboard-card-light dark:bg-dashboard-card-dark rounded-card shadow-card p-5 dashboard-card relative">
      <DataStatusIndicator status={dataStatus} lastUpdated={lastUpdated} />
      <div className="flex justify-between items-start mb-4">
        <h3 className="text-lg font-semibold">Google Trends - SPX6900 Price (12m)</h3>
        <div className="text-xs text-dashboard-text-muted-light dark:text-dashboard-text-muted-dark">
          Live Data
        </div>
      </div>
      
      {/* Content */}
      {!hasData ? (
        // Error state when no data available
        <div className="space-y-4">
          <div className="flex items-center justify-center py-8">
            <div className="text-center">
              <div className="text-red-500 text-4xl mb-2">⚠️</div>
              <p className="text-lg font-semibold text-red-500">Data Unavailable</p>
              <p className="text-sm text-dashboard-text-muted-light dark:text-dashboard-text-muted-dark mt-2">
                Unable to fetch Google Trends data. API may be unavailable or rate limited.
              </p>
              <a 
                href="https://trends.google.com/trends/explore?q=SPX6900%20price&hl=en&date=today%2012-m" 
                target="_blank" 
                rel="noopener noreferrer" 
                className="text-blue-500 hover:text-blue-600 underline text-sm mt-3 inline-block"
              >
                View on Google Trends →
              </a>
            </div>
          </div>
        </div>
      ) : (
        // Normal data display
        <div className="space-y-4">
          {/* Current trend value */}
          <div className="flex items-center justify-between">
            <div className="text-3xl font-bold">{displayValue}</div>
            <div 
              className="px-3 py-1 rounded-full text-sm font-medium"
              style={{ 
                backgroundColor: `${getInterestColor(displayValue)}20`,
                color: getInterestColor(displayValue)
              }}
            >
              {getInterestLevel(displayValue)}
            </div>
          </div>

          {/* Trend indicator */}
          <div>
            <div className="flex justify-between text-xs text-dashboard-text-muted-light dark:text-dashboard-text-muted-dark mb-1">
              <span>Low</span>
              <span>High</span>
            </div>
            <div className="h-2 bg-gray-200 dark:bg-gray-700 rounded-full overflow-hidden">
              <div 
                className="h-full rounded-full" 
                style={{
                  width: `${displayValue}%`,
                  backgroundColor: getInterestColor(displayValue)
                }}
              />
            </div>
          </div>

          {/* Summary text */}
          <div className="text-sm text-dashboard-text-muted-light dark:text-dashboard-text-muted-dark">
            <p>Search interest for "SPX6900 price" is currently at {displayValue}/100, indicating {getInterestLevel(displayValue).toLowerCase()}.</p>
            <p className="mt-2 text-xs">
              <a 
                href="https://trends.google.com/trends/explore?q=SPX6900%20price&hl=en&date=today%2012-m" 
                target="_blank" 
                rel="noopener noreferrer" 
                className="text-blue-500 hover:text-blue-600 underline"
              >
                View on Google Trends
              </a>
            </p>
          </div>
        </div>
      )}
    </div>
  );
};

export default GoogleTrendsCard;